import { z } from 'zod';

// Login
export const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});
export type LoginFormData = z.infer<typeof loginSchema>;

// Register
export const registerSchema = z
  .object({
    fullName: z.string().min(1, 'Full name is required').trim(),
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: z
      .string()
      .min(8, 'Minimum 8 characters')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/\d/, 'Must contain a number')
      .regex(/[!@#$%^&*()\-_+=]/, 'Must contain a special character'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    role: z.enum(['owner', 'worker']),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type RegisterFormData = z.infer<typeof registerSchema>;

// Copy modal
export const copyModalSchema = z.object({
  newName: z.string().min(1, 'Name is required').trim(),
});
export type CopyModalFormData = z.infer<typeof copyModalSchema>;

// Assignment
export const editAssignmentSchema = z.object({
  workerId: z.string().min(1, 'Worker is required'),
  dueDate: z.string().optional(),
  eventDate: z.string().optional(),
});
export type EditAssignmentFormData = z.infer<typeof editAssignmentSchema>;

export const copyAssignmentSchema = z.object({
  workflowId: z.string().min(1, 'Workflow is required'),
  workerId: z.string().min(1, 'Please select a team member to assign'),
  dueDate: z.string().optional(),
});
export type CopyAssignmentFormData = z.infer<typeof copyAssignmentSchema>;

// Widget builder
export const widgetBuilderSchema = z
  .object({
    name: z.string().trim().default(''),
    description: z.string().trim().default(''),
    widgetType: z.string().nullable().default(null),
    config: z.any().nullable().default(null),
  })
  .superRefine((data, ctx) => {
    if (!data.widgetType || !data.config) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please complete all configuration steps',
        path: ['config'],
      });
      return;
    }

    // Name + description required
    if (!data.name?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Widget name is required',
        path: ['name'],
      });
    }
    if (!data.description?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Widget description is required',
        path: ['description'],
      });
    }
  });
export type WidgetBuilderFormData = z.infer<typeof widgetBuilderSchema>;

// Asset (Coverage)
export const assetSchema = z.object({
  name: z.string().min(1, 'Please select an asset from the search results').trim(),
  description: z.string().optional().default(''),
  dataUid: z.string().min(1, 'Please select an asset from the search results'),
  type: z.enum(['company', 'fund', 'property', 'other']),
  sector: z.string().optional().default(''),
});
export type AssetFormData = z.infer<typeof assetSchema>;
